import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import {
  Table,
  TableProps,
  NonCancelableCustomEvent,
  Box,
  Header,
} from "@amzn/awsui-components-react-v3";
import { GetWorkflowsResponseDto } from "./get-workflows-response.dto";
import { JobObject } from "@amzn/squad-front-end-shared-react-components";

interface RenderedJob extends JobObject {
  description: string | React.ReactNode;
  completedOn: number;
  hideRetry?: boolean;
}
import JobStatus, { JobState } from "./JobStatus";
import styles from "./myjobs.module.css";
import { LoadingState } from "../../http";
import { TableColumnSort } from "../common/TableColumnSort";
import useHttpRequest from "../../http/useHttpRequest";
import InfoLink from "../../common/infoLink/InfoLink";
import { MetricsState } from "../../logging";
import { LocalStorageStateKeys } from "../../common/userPreferences/LocalStorageStateKeys";
import { useLocalStorageState } from "@amzn/squad-front-end-shared-react-components";
import { DownloadLink } from "./DownloadLink";

export interface JobDescriptionProps {
  job: JobObject;
}

export interface JobTypeProps {
  job: JobObject;
}

export interface MyJobsProps {
  /**
   * HTTP request metrics state object.
   */
  metrics: MetricsState;
  /**
   * Url the jobs page should use to get a list of workflow jobs (GET).
   */
  getWorkflowsUrl: string;
  /**
   * Url the jobs page should use to retry a particular workflow job (PUT).
   */
  getRetryWorkflowUrl: (job: JobObject) => string;
  /**
   * Gets a user-friendly description of the job.
   */
  getJobDescription: (props: JobDescriptionProps) => string | React.ReactNode;
  /**
   * Gets the formatted job type / name of the workflow job.
   */
  getFormattedJobType: (props: JobTypeProps) => string | React.ReactNode;
  /**
   * Allows the consumer to filter which workflows are shown in the table.  Return true
   * to show the workflow, or false to remove it.
   */
  workflowFilter: (job: JobObject) => boolean;
  /**
   * Help panel display function for when the info link is clicked
   */
  onInfoLinkClick?: () => void;
}

const RETRYING_STATE = "Retrying";

const getCompletedOn = (workflow: JobObject): number => {
  return (Object.values(JobState) as string[]).includes(workflow.state)
    ? workflow.lastModifiedOn
    : 0;
};

const getFormattedDate = (date: number | undefined): string => {
  if (date && date !== 0) {
    const dateObject = DateTime.fromSeconds(date);
    return `${dateObject.toFormat("t ")}${
      dateObject.offsetNameShort
    }, ${dateObject.toFormat("DDD")}`; // comes out as something like 12:06 PM PDT, July 25, 2019
  }
  return "-";
};

export const MyJobs = (properties: MyJobsProps): JSX.Element => {
  const {
    metrics,
    getWorkflowsUrl,
    getJobDescription,
    getRetryWorkflowUrl,
    getFormattedJobType,
    workflowFilter,
    onInfoLinkClick,
  } = properties;

  const [jobsList, setJobsList] = useState<RenderedJob[]>([]);
  const [processedTokens, setProcessedTokens] = useState<Set<string>>(
    new Set()
  );

  const { loadingState, data, error, sendRequest } = useHttpRequest<
    GetWorkflowsResponseDto
  >(
    "jobsFetch",
    { config: metrics.config },
    {
      url: getWorkflowsUrl,
      method: "GET",
      params: {
        pageSize: 1000,
      },
    }
  );

  const { sendRequest: sendRetryRequest } = useHttpRequest("jobRetry", {
    config: metrics.config,
  });

  const [sortColumn, setSortColumn] = useLocalStorageState<TableColumnSort>(
    LocalStorageStateKeys.MYJOBS_TABLE_SORT_COLUMN,
    {
      column: "createdOn",
      descending: true,
    }
  );
  const handleSortChange = (
    event: NonCancelableCustomEvent<{
      sortingColumn: { sortingField?: string };
      sortingDescending?: boolean;
    }>
  ): void => {
    const newColumn = event.detail.sortingColumn.sortingField || "";
    const isDescending = event.detail.sortingDescending ?? false;
    if (sortColumn.column === newColumn) {
      setSortColumn({
        column: newColumn,
        descending: !sortColumn.descending,
      });
    } else {
      setSortColumn({
        column: newColumn,
        descending: isDescending,
      });
    }
  };

  useEffect(() => {
    if (loadingState !== LoadingState.RESOLVED || !data) return;

    const token = data.nextToken || "initial";
    if (processedTokens.has(token)) return;

    const renderedJobs = data.workflows
      .filter(workflowFilter)
      .map((workflow) => {
        // calculate derived fields here because they are needed in order for
        // table sorting to work correctly.
        const renderedJob = workflow as RenderedJob;
        renderedJob.description = getJobDescription({ job: workflow });
        renderedJob.completedOn = getCompletedOn(workflow);
        return renderedJob;
      });
    setJobsList(jobsList.concat(renderedJobs));
    setProcessedTokens(new Set(processedTokens).add(token));
    if (!!data.nextToken) {
      sendRequest({
        url: getWorkflowsUrl,
        method: "GET",
        params: {
          pageSize: 1000,
          nextToken: data.nextToken,
        },
      });
    }
  }, [
    loadingState,
    data,
    sendRequest,
    getWorkflowsUrl,
    getJobDescription,
    workflowFilter,
  ]);

  const setJobPending = (job: JobObject): void => {
    const updatedJobObject: JobObject = {
      ...job,
      state: RETRYING_STATE,
    };
    const updatedJob: RenderedJob = {
      ...updatedJobObject,
      description: getJobDescription({ job: updatedJobObject }),
      completedOn: getCompletedOn(updatedJobObject),
    };

    const updatedJobsList = jobsList
      .filter(({ instanceId }) => instanceId !== updatedJob.instanceId)
      .concat(updatedJob);
    setJobsList(updatedJobsList);
  };

  const retryJob = (job: JobObject): void => {
    setJobPending(job);
    sendRetryRequest({
      url: getRetryWorkflowUrl(job),
      method: "PUT",
    });
  };

  const getJobStatus = (job: JobObject): React.ReactNode => {
    return (
      <JobStatus state={job.state.toString()} onRetry={() => retryJob(job)} />
    );
  };

  const columnDefinitions: TableProps.ColumnDefinition<RenderedJob>[] = [
    {
      id: "name",
      header: "Job type",
      cell: (item: RenderedJob) => getFormattedJobType({ job: item }),
      width: 150,
      sortingField: "name",
    },
    {
      id: "description",
      header: "Description",
      cell: (item: RenderedJob) => item.description,
      width: 350,
      sortingField: "description",
    },
    {
      id: "state",
      header: "Status",
      cell: (item: RenderedJob) => getJobStatus(item),
      width: 150,
      sortingField: "state",
    },
    {
      id: "createdOn",
      header: "Submitted on",
      cell: (item: RenderedJob) => getFormattedDate(item.createdOn),
      width: 300,
      sortingField: "createdOn",
    },
    {
      id: "completedOn",
      header: "Completed on",
      cell: (item: RenderedJob) => getFormattedDate(item.completedOn),
      width: 250,
      sortingField: "completedOn",
    },
    {
      id: "downloadErrorfile",
      header: "",
      cell: (item: RenderedJob) => {
        if (
          (item.name === "CONVERT_UNAPPROVED_POSITIONS" &&
            item.state === "FAILED") ||
          (item.name === "EXPORT" &&
            item.state === "COMPLETED" &&
            Date.parse(item.summary.expire) > Date.now())
        )
          return (
            <DownloadLink
              metrics={metrics}
              job={item}
              apiUrl={getWorkflowsUrl}
            />
          );
        else return <></>;
      },
      width: 150,
    },
  ];

  const getSortedJobs = (jobs: RenderedJob[]): RenderedJob[] => {
    if (!sortColumn.column) return jobs;

    return [...jobs].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn.column) {
        case "createdOn":
          comparison = (a.createdOn || 0) - (b.createdOn || 0);
          break;
        case "completedOn":
          comparison = (a.completedOn || 0) - (b.completedOn || 0);
          break;
        case "name":
          comparison = (a.name || "").localeCompare(b.name || "");
          break;
        case "state":
          comparison = (a.state || "").localeCompare(b.state || "");
          break;
        case "description":
          const aDesc = typeof a.description === "string" ? a.description : "";
          const bDesc = typeof b.description === "string" ? b.description : "";
          comparison = aDesc.localeCompare(bDesc);
          break;
        default:
          return 0;
      }

      return sortColumn.descending ? -comparison : comparison;
    });
  };

  const visibleJobs = getSortedJobs(jobsList.filter(workflowFilter));

  return (
    <div>
      <div className={styles["jobs-header-wrapper"]}>
        <div className={styles["jobs-header"]}>
          <Header
            variant="h1"
            info={
              onInfoLinkClick ? (
                <InfoLink onClick={onInfoLinkClick} />
              ) : (
                undefined
              )
            }
          >
            System tasks
          </Header>
          <Box>Your pending and completed system tasks.</Box>
        </div>
      </div>
      <div className={styles["jobs-page-wrapper"]}>
        <div className={styles["jobs-content-wrapper"]}>
          <Table
            loadingText="Loading resources"
            loading={loadingState !== LoadingState.RESOLVED}
            columnDefinitions={columnDefinitions}
            items={visibleJobs}
            wrapLines
            sortingColumn={{ sortingField: sortColumn.column }}
            sortingDescending={sortColumn.descending}
            onSortingChange={handleSortChange}
            empty={
              <Box textAlign="center">
                <Box padding={{ top: "xs", bottom: "xs" }}>
                  <b>No jobs</b>
                </Box>
                <Box padding={{ bottom: "s" }}>No jobs to display</Box>
                <Box padding={{ bottom: "l" }} />
              </Box>
            }
          />
        </div>
      </div>
    </div>
  );
};
